import React, { useState } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Eye, Calendar, User, Flag } from "lucide-react"
import { TaskDetailModal } from "@/components/tasks/task-detail-modal"

const TaskTable = ({ tasks, title, emptyMessage, csrf_token, users = [], categories = [] }) => {
  const [selectedTask, setSelectedTask] = useState(null)
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false)

  const handleTaskClick = (task) => {
    
    // Format due_date properly for the modal
    let formattedDueDate = null
    if (task.due_date) {
      // If it's already a date string in YYYY-MM-DD format, use it as is
      if (typeof task.due_date === 'string' && task.due_date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        formattedDueDate = task.due_date
      } else {
        // Otherwise, try to format it
        try {
          const date = new Date(task.due_date)
          if (!isNaN(date.getTime())) {
            formattedDueDate = date.toISOString().split('T')[0] // YYYY-MM-DD format
          }
        } catch (e) {
          console.warn('Could not format due_date:', task.due_date)
        }
      }
    }

    // Map the task data to match what TaskDetailModal expects
    const mappedTask = {
      id: task.id || task.task_id,
      name: task.name || task.task_name,
      description: task.description || '',
      status: task.status || 'to_do',
      priority: task.priority || '5',
      category: task.category || '',
      assignee_id: task.assignee_id || null,
      assignee: task.assignee || null,
      // TaskDetailModal expects these field names for due date
      due_date: formattedDueDate,
      due: formattedDueDate,
      dueDate: formattedDueDate,
      defer_date: task.defer_date || null,
      // TaskDetailModal expects users array for assignee
      users: task.users && task.users.length > 0 ? task.users : (task.assignee_id ? [{ id: task.assignee_id, name: task.assignee }] : []),
      link: task.link || '',
      link_title: task.link_title || '',
      deferrable: task.deferrable || 0,
      missable: task.missable || 0,
      refid: task.refid || '',
      created_at: task.created_at || null,
      updated_at: task.updated_at || null,
      // Additional fields that might be needed
      workflow_id: task.workflow_id || null,
      step_id: task.step_id || null,
      step_name: task.step_name || null
    }
    
    setSelectedTask(mappedTask)
    setIsTaskModalOpen(true)
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">✅ Completed</Badge>
      case 'in_progress':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800 border-blue-200">🔄 In Progress</Badge>
      case 'to_do':
        return <Badge variant="outline" className="bg-gray-100 text-gray-800 border-gray-200">📋 To Do</Badge>
      default:
        return <Badge variant="outline">{status}</Badge>
    }
  }

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case '1':
        return <Badge variant="destructive" className="bg-red-100 text-red-800 border-red-200">🔴 High</Badge>
      case '2':
        return <Badge variant="destructive" className="bg-orange-100 text-orange-800 border-orange-200">🟠 Medium-High</Badge>
      case '3':
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 border-yellow-200">🟡 Medium</Badge>
      case '4':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800 border-blue-200">🔵 Low-Medium</Badge>
      case '5':
        return <Badge variant="outline" className="bg-gray-100 text-gray-800 border-gray-200">⚪ Low</Badge>
      default:
        return <Badge variant="outline">Priority {priority}</Badge>
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  if (!tasks || tasks.length === 0) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">{title}</h4>
        <div className="text-center text-gray-500 dark:text-gray-400 py-8">
          {emptyMessage}
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">{title} ({tasks.length} tasks)</h4>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md">
          {/* <ScrollArea className="h-60"> */}
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {tasks.map((task, index) => (
                <div 
                  key={index} 
                  className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                  onClick={() => handleTaskClick(task)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h5 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {task.task_name || 'Unnamed Task'}
                        </h5>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 hover:bg-gray-200 dark:hover:bg-gray-700"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleTaskClick(task)
                          }}
                        >
                          <Eye className="h-3 w-3" />
                        </Button>
                      </div>
                      
                      <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                        <div className="flex items-center gap-1">
                          <Flag className="h-3 w-3" />
                          <span>ID: {task.task_id}</span>
                        </div>
                        {task.refid && (
                          <div className="flex items-center gap-1">
                            <span className="font-mono">Ref: {task.refid}</span>
                          </div>
                        )}
                        {task.assignee && (
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            <span>{task.assignee}</span>
                          </div>
                        )}
                        {task.due_date && (
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            <span>{formatDate(task.due_date)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 ml-4">
                      {getStatusBadge(task.status)}
                      {getPriorityBadge(task.priority)}
                    </div>
                  </div>
                  
                  {task.step_name && (
                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      <span className="font-medium">From step:</span> {task.step_name}
                    </div>
                  )}
                </div>
              ))}
            </div>
          {/* </ScrollArea> */}
        </div>
      </div>

      {/* Task Detail Modal */}
      {selectedTask && (
        <TaskDetailModal
          open={isTaskModalOpen}
          onOpenChange={setIsTaskModalOpen}
          task={selectedTask}
          onComplete={() => {}}
          onDefer={() => {}}
          onAddNote={() => {}}
          onUpdateStatus={() => {}}
          isSubmitting={false}
          onViewParent={() => {}}
          availableWorkflows={[]}
          users={users}
          categories={categories}
          csrf_token={csrf_token}
        />
      )}
    </>
  )
}

export default TaskTable
